// status-operating-table.ts
//
// P13 Agent C: the operator-facing table `avatar autopilot status` renders —
// one row per ticket (id/kind/slice/status/runs · counted failures/last
// verdict/running-since) plus a mission summary line (status == the closest
// thing this codebase has to a "phase" — mission_status_changed already
// carries values like "blocked:quiet_hours" — gates awaited (+request_id),
// quota wake_at from quota-sleep.json). Pure data-shaping: all I/O (ticket
// frontmatter, attempt-state.json, quota-sleep.json, gates.json,
// effective-config.json) happens in status-renderer.ts's readStatusSnapshot,
// this module only turns already-read values into rows — keeps it testable
// without tmpdir fixtures for every row-shape case.
import type { GateFile, GateId } from "./gate-registry.js";
import type { Interview } from "./interview-store.js";
import type { TicketVerdictRecord } from "./mission-state-reducer.js";
import { isAwake, type QuotaSleepState } from "./quota-sleep-scheduler.js";
import { settleAnsweredNeedsDecisionVerdict } from "./status-ticket-row-answered-needs-decision.js";
import type { AttemptStateFile } from "./supervisor-attempt-state.js";

export interface TicketOperatingRow {
  id: string;
  kind: string;
  sliceId: string;
  status: string;
  /** 1-based "current attempt" — physical_runs + 1 when the ticket is still open, else physical_runs (no attempt is "in flight" once terminal). Kept for `--json`; the text/HTML cell reads `physicalRuns` instead (06f R3). */
  attemptCurrent: number;
  /** Ceiling of COUNTED FAILURES (max_counted_failures), not of physical runs — undefined when effective-config.json wasn't readable (best-effort). */
  attemptTotal: number | undefined;
  /** 06f R3 (F-31): attempt-state.json `physical_runs` as-is (0 when absent, never +1). */
  physicalRuns: number;
  /** 06f R3 (F-31): attempt-state.json `counted_failures` as-is (0 when absent). */
  countedFailures: number;
  lastVerdictCategory: string | undefined;
  lastVerdictReasons: string[];
}

export interface TicketForRow {
  id: string;
  kind: string;
  slice_id: string;
}

export function buildTicketOperatingRows(
  tickets: TicketForRow[],
  ticketStatus: Record<string, string>,
  attemptState: AttemptStateFile,
  ticketLastVerdict: Record<string, TicketVerdictRecord>,
  maxCountedFailures: number | undefined,
  /** 06j R6 (F-50): settles "chờ người trả lời" once interview.json has the answer. */
  interview?: Interview,
): TicketOperatingRow[] {
  return tickets.map((ticket) => {
    const status = ticketStatus[ticket.id] ?? "todo";
    const retryState = attemptState.tickets[ticket.id];
    const physicalRuns = retryState?.physical_runs ?? 0;
    // A ticket still open (todo/in_progress/blocked-but-retryable) is ABOUT
    // to run attempt physicalRuns+1 next; a terminal ticket (done/blocked
    // for good/cancelled/superseded) already used its last physical_runs
    // value as its final attempt number — no "next" attempt exists.
    const isOpen = status === "todo" || status === "in_progress";
    const attemptCurrent = isOpen ? physicalRuns + 1 : physicalRuns;
    const verdict = settleAnsweredNeedsDecisionVerdict(
      ticket.id,
      visibleLastVerdict(status, ticketLastVerdict[ticket.id]),
      interview,
    );
    return {
      id: ticket.id,
      kind: ticket.kind,
      sliceId: ticket.slice_id,
      status,
      attemptCurrent,
      attemptTotal: maxCountedFailures,
      physicalRuns,
      countedFailures: retryState?.counted_failures ?? 0,
      lastVerdictCategory: verdict?.category,
      lastVerdictReasons: verdict?.reasons ?? [],
    };
  });
}

/** 06f R3 (F-31): a `done` ticket's last verdict is history, not a reason — hidden from text/HTML (`ticket_last_verdict` in `--json` stays raw). */
export function visibleLastVerdict<V>(status: string, verdict: V | undefined): V | undefined {
  return status === "done" ? undefined : verdict;
}

/** 06f R3 (F-31): one cell for text + HTML — the two attempt-state.json counters, never mixed into one fraction. */
export function formatAttemptCell(
  row: Pick<TicketOperatingRow, "physicalRuns" | "countedFailures" | "attemptTotal">,
): string {
  return `lượt ${row.physicalRuns} · lỗi ${row.countedFailures}/${row.attemptTotal ?? "?"}`;
}

export interface PendingGateRequest {
  gate: GateId;
  request_id: string | undefined;
}

export interface MissionOperatingSummary {
  missionId: string | undefined;
  /** Mission's own status string (RUNNING / blocked:<code> / DONE / "paused" / ...) — the closest thing to a "phase" this codebase journals. */
  status: string | undefined;
  /** Every gate (g0..g3 order) currently "pending", with its request_id — [] when none. */
  pendingGates: PendingGateRequest[];
  /** quota-sleep.json's wake_at, when a SLEEPING episode is on file — undefined otherwise. */
  quotaWakeAt: string | undefined;
  /** Phase 05 Req 8: attempts counted in `attempts_total` that never emitted `attempt_usage_recorded` (killed/SIGINT'd/unlocked) — 0 when every attempt has usage. */
  attemptsWithoutUsage: number;
  /** V2-1 (§4.1): dòng `decision_policy` trong BLUEPRINT đã duyệt không đọc được ⇒ hiệu lực `ask`. Vắng khi không có gì để báo. */
  decisionPolicyWarning?: string;
  /** 06g R5: mission spend so far (undefined ⇒ no usage recorded yet) — only set together with `costLimitUsd`. */
  costUsd?: number;
  /** 06g R5: `max_cost_usd_per_mission` the mission runs under; absent ⇒ no cost line (pre-R5 callers). */
  costLimitUsd?: number;
}

const GATE_ORDER: GateId[] = ["g0", "g1", "g2", "g3"];

export function listPendingGateRequests(gates: GateFile | undefined): PendingGateRequest[] {
  if (gates === undefined) return [];
  return GATE_ORDER.filter((id) => gates[id]?.status === "pending").map((gate) => ({
    gate,
    request_id: gates[gate]?.request_id,
  }));
}

export function formatPendingGateRequests(pending: readonly PendingGateRequest[]): string {
  return pending.map((p) => `${p.gate} [${p.request_id ?? "?"}]`).join(", ");
}

export function buildMissionOperatingSummary(
  missionId: string | undefined,
  status: string | undefined,
  gates: GateFile | undefined,
  quotaState: QuotaSleepState | undefined,
  attemptsWithoutUsage = 0,
  cost?: { usd: number | undefined; limitUsd: number },
  now: () => Date = () => new Date(),
): MissionOperatingSummary {
  return {
    missionId,
    status,
    pendingGates: listPendingGateRequests(gates),
    // 06h R5a (F-41): a wake_at already passed is history, not a state to print.
    quotaWakeAt:
      quotaState !== undefined && !isAwake(quotaState, now()) ? quotaState.wake_at : undefined,
    attemptsWithoutUsage,
    ...(cost === undefined ? {} : { costUsd: cost.usd, costLimitUsd: cost.limitUsd }),
  };
}

/** Req 7 (D8/B15): shared by `renderStatusTableText` and `autopilot.ts`'s fallback line (DRY) — `"paused"` gets the resume hint appended. */
export function formatMissionStatusLine(
  missionId: string | undefined,
  status: string | undefined,
): string {
  const base = `Mission ${missionId ?? "(chưa có)"}: ${status ?? "(unknown)"}`;
  return status === "paused" ? `${base} — chạy avatar autopilot resume` : base;
}

// 06g R5: the text table moved to status-operating-table-text.ts (this file hit
// the 200-line budget); re-exported so every importer keeps its path.
export { renderStatusTableText } from "./status-operating-table-text.js";
